import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAppState } from "../context/AppState";

function viewerKey() {
  let key = localStorage.getItem("dh_story_viewer_key");
  if (!key) {
    key = `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("dh_story_viewer_key", key);
  }
  return key;
}

function compactCount(value) {
  const amount = Number(value || 0);
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
  return String(amount);
}

function money(value) {
  return `S/ ${Number(value || 0).toFixed(2)}`;
}

function remainingLabel(expiresAt) {
  const remaining = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(remaining) || remaining <= 0) return "Promoción finalizada";
  const hours = Math.floor(remaining / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function StoryIcon({ name }) {
  const icons = {
    heart: <path d="M20.8 4.8a5.5 5.5 0 0 0-7.8 0L12 5.9l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.4a5.5 5.5 0 0 0 0-7.8Z" />,
    eye: <><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" /><circle cx="12" cy="12" r="2.5" /></>,
    save: <path d="M6 3h12v18l-6-4-6 4V3Z" />,
    share: <><circle cx="18" cy="5" r="2.5" /><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="19" r="2.5" /><path d="m8.2 10.8 7.6-4.5M8.2 13.2l7.6 4.5" /></>,
  };
  return <svg viewBox="0 0 24 24" aria-hidden="true">{icons[name]}</svg>;
}

export default function StoriesStrip() {
  const { address } = useAppState();
  const [stories, setStories] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [progress, setProgress] = useState(0);
  const [soundRequired, setSoundRequired] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [countdown, setCountdown] = useState("");
  const [notice, setNotice] = useState("");
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const noticeRef = useRef(null);
  const videoRef = useRef(null);
  const active = activeIndex === null ? null : stories[activeIndex];

  useEffect(() => {
    const params = new URLSearchParams({ viewer_key: viewerKey() });
    const latitude = Number(address?.lat);
    const longitude = Number(address?.lng);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      params.set("latitude", String(latitude));
      params.set("longitude", String(longitude));
    }

    setActiveIndex(null);
    api(`/store-stories?${params.toString()}`)
      .then((response) => setStories(response?.stories || []))
      .catch(() => setStories([]));
  }, [address?.lat, address?.lng]);

  const groups = useMemo(() => {
    const seen = new Set();
    return stories.filter((story) => {
      const key = String(story.restaurant?.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [stories]);

  function openStory(id) {
    const index = stories.findIndex((story) => story.id === id);
    if (index >= 0) setActiveIndex(index);
  }

  function closeViewer() {
    setActiveIndex(null);
  }

  function next() {
    setActiveIndex((current) => {
      if (current === null || stories.length === 0) return null;
      return (current + 1) % stories.length;
    });
  }

  function previous() {
    setActiveIndex((current) => {
      if (current === null || stories.length === 0) return null;
      return (current - 1 + stories.length) % stories.length;
    });
  }

  function showNotice(message) {
    window.clearTimeout(noticeRef.current);
    setNotice(message);
    noticeRef.current = window.setTimeout(() => setNotice(""), 2200);
  }

  useEffect(() => {
    document.body.classList.toggle("story-viewer-open", Boolean(active));
    const closeOnEscape = (event) => {
      if (event.key === "Escape") closeViewer();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("story-viewer-open");
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [Boolean(active)]);

  useEffect(() => {
    window.clearInterval(timerRef.current);
    window.clearInterval(countdownRef.current);
    setProgress(0);
    setSoundRequired(false);
    setMediaReady(false);
    setMediaError(false);
    if (!active) return undefined;

    api(`/store-stories/${active.id}/view`, {
      method: "POST",
      body: JSON.stringify({ viewer_key: viewerKey() }),
    })
      .then((result) => {
        if (result?.view_count === undefined) return;
        setStories((current) => current.map((story) => (
          story.id === active.id ? { ...story, view_count: result.view_count } : story
        )));
      })
      .catch(() => {});

    const updateCountdown = () => setCountdown(remainingLabel(active.expires_at));
    updateCountdown();
    countdownRef.current = window.setInterval(updateCountdown, 1000);

    if (active.type !== "VIDEO") {
      setMediaReady(true);
      const started = Date.now();
      timerRef.current = window.setInterval(() => {
        const nextProgress = Math.min(100, ((Date.now() - started) / 6000) * 100);
        setProgress(nextProgress);
        if (nextProgress >= 100) {
          window.clearInterval(timerRef.current);
          next();
        }
      }, 100);
    }
    return () => {
      window.clearInterval(timerRef.current);
      window.clearInterval(countdownRef.current);
    };
  }, [active?.id]);

  useEffect(() => {
    if (!active || active.type !== "VIDEO") return undefined;
    const frame = window.requestAnimationFrame(() => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = 0;
      video.muted = false;
      video.volume = 1;
      const playback = video.play();
      if (playback?.catch) {
        playback.catch(() => {
          video.muted = true;
          video.play().catch(() => {});
          setSoundRequired(true);
        });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [active?.id]);

  function enableSound() {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    video.volume = 1;
    video.play().then(() => setSoundRequired(false)).catch(() => {});
  }

  function toggleVideo() {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }

  async function toggle(reaction) {
    if (!active) return;
    try {
      const result = await api(`/store-stories/${active.id}/${reaction}`, {
        method: "POST",
        body: JSON.stringify({ viewer_key: viewerKey() }),
      });
      setStories((current) => current.map((story) => story.id === active.id ? {
        ...story,
        [reaction === "like" ? "liked" : "saved"]: result[reaction === "like" ? "liked" : "saved"],
        [reaction === "like" ? "like_count" : "save_count"]: result[reaction === "like" ? "like_count" : "save_count"],
      } : story));
    } catch {
      showNotice("Este estado ya no está disponible");
    }
  }

  async function shareStory() {
    if (!active) return;
    const target = active.action_url
      || (active.is_platform ? "/explore" : `/food/stores/${active.restaurant.slug}`);
    const url = new URL(target, window.location.origin).href;
    if (navigator.share) {
      await navigator.share({ title: active.title, text: active.description || active.title, url }).catch(() => {});
      return;
    }
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(url).then(() => showNotice("Enlace copiado")).catch(() => {});
    }
  }

  if (!stories.length) return null;

  return (
    <>
      <section className="stories-strip" aria-label="Estados y promociones">
        <header>
          <small>NOVEDADES QUE VENCEN PRONTO</small>
          <button type="button" onClick={() => setActiveIndex(0)}>Ver todos</button>
        </header>
        <div className="stories-strip__scroll">
          {groups.map((story) => (
            <button className="story-bubble" key={story.restaurant.id} onClick={() => openStory(story.id)} type="button">
              <span><img src={story.restaurant.image} alt="" /></span>
              <strong>{story.restaurant.name}</strong>
              {story.is_platform ? <em>OFICIAL</em> : story.discount_percent ? <em>-{Math.round(story.discount_percent)}%</em> : null}
            </button>
          ))}
        </div>
      </section>

      {active && (
        <div className="story-viewer" role="dialog" aria-modal="true" aria-label={active.title} onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeViewer();
        }}>
          <div className="story-viewer__shell">
            <div className="story-viewer__progress">
              {stories.map((story, index) => (
                <span key={story.id}>
                  <i style={{ width: `${index < activeIndex ? 100 : index === activeIndex ? progress : 0}%` }} />
                </span>
              ))}
            </div>

            <header>
              <img src={active.restaurant.image} alt="" />
              <div>
                <b>{active.restaurant.name}</b>
                <small>{active.is_platform ? "Promoción oficial" : "Delivery Huaraz"} · ahora</small>
              </div>
              <button onClick={closeViewer} type="button" aria-label="Cerrar">×</button>
            </header>

            <main className={`story-viewer__media ${mediaReady ? "is-ready" : ""} ${mediaError ? "has-error" : ""}`}>
              {!mediaReady && (
                <div className="story-viewer__loading">
                  {!mediaError && <span />}
                  <small>{mediaError ? "No se pudo cargar el video" : "Preparando video…"}</small>
                </div>
              )}
              {active.type === "VIDEO" ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  preload="auto"
                  disablePictureInPicture
                  src={active.media_path}
                  onClick={toggleVideo}
                  onPlaying={() => setMediaReady(true)}
                  onCanPlay={(event) => event.currentTarget.play().catch(() => {})}
                  onError={() => {
                    setMediaError(true);
                    window.setTimeout(next, 3000);
                  }}
                  onTimeUpdate={(event) => {
                    const video = event.currentTarget;
                    if (video.duration) setProgress((video.currentTime / video.duration) * 100);
                  }}
                  onEnded={next}
                />
              ) : (
                <img src={active.media_path} alt={active.title} />
              )}
              {soundRequired && (
                <button className="story-viewer__sound" onClick={enableSound} type="button">
                  🔊 Activar sonido
                </button>
              )}
            </main>

            <aside className="story-viewer__actions" aria-label="Acciones del estado">
              <button className={active.liked ? "is-active" : ""} onClick={() => toggle("like")} type="button" aria-label="Me gusta">
                <StoryIcon name="heart" /><span>{compactCount(active.like_count)}</span>
              </button>
              <div aria-label={`${active.view_count || 0} visualizaciones`}>
                <StoryIcon name="eye" /><span>{compactCount(active.view_count)}</span>
              </div>
              <button className={active.saved ? "is-active" : ""} onClick={() => toggle("save")} type="button" aria-label="Guardar">
                <StoryIcon name="save" /><span>{compactCount(active.save_count)}</span>
              </button>
              <button onClick={shareStory} type="button" aria-label="Compartir">
                <StoryIcon name="share" /><span>Compartir</span>
              </button>
            </aside>

            <footer className="story-viewer__copy">
              <h2>{active.title}</h2>
              {active.description && <p>{active.description}</p>}
              {active.discount_percent && (
                <div className="story-viewer__price">
                  <span>Antes <del>{money(active.original_price)}</del></span>
                  <strong>{money(active.promotional_price)}</strong>
                  <b>-{Math.round(active.discount_percent)}%</b>
                </div>
              )}
              <div className="story-viewer__countdown">
                {countdown === "Promoción finalizada" ? countdown : <><small>TERMINA EN</small><strong>{countdown}</strong></>}
              </div>
              {active.action_url && (/^https?:\/\//i.test(active.action_url) ? (
                <a href={active.action_url}>{active.action_label || "Ver promoción"}</a>
              ) : (
                <Link to={active.action_url} onClick={closeViewer}>
                  {active.action_label || "Ver promoción"}
                </Link>
              ))}
            </footer>

            <div className={`story-viewer__notice ${notice ? "is-visible" : ""}`} role="status">{notice}</div>
            <button className="story-viewer__previous" onClick={previous} type="button" aria-label="Estado anterior">‹</button>
            <button className="story-viewer__next" onClick={next} type="button" aria-label="Siguiente estado">›</button>
          </div>
        </div>
      )}
    </>
  );
}
